import React, { FC, memo, useMemo } from "react";
import {
  DrawerBody,
  DrawerHeader,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Text,
  Box,
  HStack,
  Spacer,
  Divider,
  Center,
  Spinner,
  Stack,
} from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import moment from "moment";

import UserAvatar from "modules/_shared/user-avatar";
import UserName from "modules/_shared/user-name";
import colors from "app/colors";
import {
  EyeIcon,
  EyeSlashIcon,
  MailPersonIcon,
  PhoneSmsIcon,
} from "modules/icons";
import { useUsers } from "modules/providers/users-provider";
import UserMention from "modules/_shared/mentions/user-mention";
import {
  ReactionType,
  MessageProfileQuery,
  MessageProfileQueryVariables,
  UserFragment,
  MessageThreadsQuery,
  MessageThreadsQueryVariables,
} from "gql/graphql";

import MESSAGE_PROFILE_QUERY from "../../drawers/networking/message-profile";
import RelatedMessageThreadList from "modules/messaging/messages/related-message-thread-list";
import MESSAGE_THREADS_QUERY from "modules/messaging/_networking/message-threads";
import {
  getUserOutOfOfficeNote,
  isUserInteractive,
  isUserSmsOnlyAndNotLoggedIn,
} from "modules/utils/Functions";
import MessageThreadMessage from "../message-thread/message-thread-message";
import ReactionsAvatarGroup from "../_shared/reactions-avatar-group";

interface IProps {
  messageId: string;
  messageThreadId: string;
}

interface MessageSeenUser {
  id: string;
  userId: string;
  seenTime: any;
  reactionTypeList: Array<ReactionType>;
  isActive: boolean;
}

const CALENDAR_FORMATS = {
  sameDay: "[Today] h:mm A",
  lastDay: "[Yesterday] h:mm A",
  lastWeek: "ddd h:mm A",
  lastElse: "ddd M/D h:mm A",
  sameElse: "ddd M/D h:mm A",
};

const MessageProfileDrawer = ({ messageThreadId, messageId }: IProps) => {
  const { usersById, getUsersById } = useUsers();

  const { data: messageProfileData, error: messageError } = useQuery<
    MessageProfileQuery,
    MessageProfileQueryVariables
  >(MESSAGE_PROFILE_QUERY, {
    variables: {
      id: messageId,
    },
  });

  const { data: messageThreadData, error: messageThreadError } = useQuery<
    MessageThreadsQuery,
    MessageThreadsQueryVariables
  >(MESSAGE_THREADS_QUERY, {
    variables: {
      id: messageThreadId,
    },
    nextFetchPolicy: "cache-only",
  });

  const messageProfile = useMemo(
    () => messageProfileData?.messages[0],
    [messageProfileData?.messages],
  );

 const relatedMessageThreads = messageProfile?.relatedMessageThreadList;

  const messageThread = useMemo(
    () => messageThreadData?.messageThreads[0],
    [messageThreadData?.messageThreads],
  );

  const seenUserList = useMemo(() => {
    if (!messageProfile) return [];
    return messageProfile.messageSeenList
      .filter(
        (messageSeen) =>
          messageSeen.userId !== messageProfile.createdUserId &&
          usersById[messageSeen.userId]?.active,
      )
      .map((messageSeen): MessageSeenUser => {
        const user = usersById[messageSeen.userId];
        return {
          id: messageSeen.id,
          userId: messageSeen.userId,
          seenTime: messageSeen.createTime,
          isActive: user?.active ?? false,
          reactionTypeList: messageProfile.reactionList
            .filter((obj) => obj.userId === messageSeen.userId && !obj.fileId)
            .map((reaction) => reaction.type),
        };
      });
  }, [messageProfile, usersById]);

  const smsOnlyUserList = useMemo(() => {
    if (!messageProfile) return [];
    return getUsersById(messageProfile.accessUserIdList).filter(
      (user) =>
        !messageProfile.seenUserIdList.includes(user.id) &&
        user.id !== messageProfile.createdUserId &&
        isUserSmsOnlyAndNotLoggedIn(user) &&
        user.active &&
        isUserInteractive(user),
    );
  }, [getUsersById, messageProfile]);

  const notSeenUserList = useMemo(() => {
    if (!messageProfile) return [];
    return getUsersById(messageProfile.accessUserIdList).filter(
      (user) =>
        !messageProfile.seenUserIdList.includes(user.id) &&
        user.id !== messageProfile.createdUserId &&
        !isUserSmsOnlyAndNotLoggedIn(user) &&
        user.active &&
        isUserInteractive(user),
    );
  }, [getUsersById, messageProfile]);

  const outsideOrganizationUserList = useMemo(() => {
    const userList: UserFragment[] = [];
    if (!messageProfile) return [];
    messageProfile.accessUserIdList.forEach((userId) => {
      const user = usersById[userId];
      if (user && !isUserInteractive(user)) {
        userList.push(user);
      }
    });
    return userList;
  }, [messageProfile, usersById]);

  if (messageError || messageThreadError) {
    return <Text>{messageError?.message ?? messageThreadError?.message}</Text>;
  }

  if (!messageThread || !messageProfile) {
    return null;
  }

  return (
    <DrawerContent>
      <DrawerCloseButton right={0} left={1} />
      <DrawerHeader>
        <Center fontWeight="bold">Message Details</Center>
      </DrawerHeader>
      <DrawerBody p={3}>
        {!messageProfile ? (
          <Center w="full" h="full">
            <Spinner />
          </Center>
        ) : (
          <Stack w="full" spacing={6}>
            <MessageThreadMessage
              messageThread={messageThread}
              message={messageProfile}
              messageState="read-only"
            />
            <RelatedMessageThreadList
              relatedMessageThreads={relatedMessageThreads}
            />
            {seenUserList.length > 0 && (
              <Stack>
                <HStack w="full">
                  <EyeIcon boxSize={7} />
                  <Text
                    fontWeight={700}
                    fontSize={18}
                  >{`Seen (${seenUserList.length})`}</Text>
                </HStack>
                <Divider borderColor="black" />
                {seenUserList.map((messageSeenUser) => (
                  <HStack w="full" key={messageSeenUser.userId}>
                    <UserItem user={usersById[messageSeenUser.userId]} />
                    <ReactionsAvatarGroup
                      reactionTypes={messageSeenUser.reactionTypeList}
                      spacing={0}
                    />
                    <Spacer />
                    <Text
                      color={colors.grayMain}
                      fontWeight={400}
                      fontSize={12}
                      whiteSpace="nowrap"
                    >
                      {moment(messageSeenUser.seenTime).calendar(
                        CALENDAR_FORMATS,
                      )}
                    </Text>
                  </HStack>
                ))}
              </Stack>
            )}
            {smsOnlyUserList.length > 0 && (
              <Stack>
                <HStack w="full" paddingTop={5}>
                  <PhoneSmsIcon boxSize={7} />
                  <Text
                    fontWeight={700}
                    fontSize={18}
                  >{`SMS-Only Users (${smsOnlyUserList.length})`}</Text>
                </HStack>
                <Divider borderColor="black" />
                {smsOnlyUserList.map((user) => (
                  <HStack w="full" key={user.id}>
                    <UserAvatar userId={user.id} size="sm" />
                    <Box marginLeft={3}>
                      <UserName userId={user.id} />
                    </Box>
                  </HStack>
                ))}
              </Stack>
            )}
            {notSeenUserList.length > 0 && (
              <Stack>
                <HStack w="full" paddingTop={5}>
                  <EyeSlashIcon boxSize={7} />
                  <Text
                    fontWeight={700}
                    fontSize={18}
                  >{`Not Seen (${notSeenUserList.length})`}</Text>
                </HStack>
                <Divider borderColor="black" />
                {notSeenUserList.map((user) => (
                  <UserItem user={user} key={user.id} />
                ))}
              </Stack>
            )}
            {outsideOrganizationUserList.length > 0 && (
              <Stack>
                <HStack w="full" paddingTop={5}>
                  <MailPersonIcon boxSize={7} fill={colors.black} />
                  <Text
                    fontWeight={700}
                    fontSize={18}
                  >{`Outside Organization (${outsideOrganizationUserList.length})`}</Text>
                </HStack>
                <Divider borderColor="black" />
                {outsideOrganizationUserList.map((user) => (
                  <HStack w="full" key={user.id}>
                    <UserAvatar userId={user.id} size="sm" />
                    <UserMention id={user.id} />
                  </HStack>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(MessageProfileDrawer);

const UserItem: FC<{
  user?: UserFragment | null;
}> = ({ user }) => {
  if (!user) {
    return null;
  }

  return (
    <HStack key={user.id} spacing={2}>
      <UserAvatar userId={user.id} size="sm" />
      <VStack w="full" spacing={0} alignItems="flex-start">
        <HStack w="full">
          <Text fontWeight={700} fontSize={14}>
            <UserMention id={user.id} />
          </Text>
          <Text color={colors.grayMain} fontWeight={400} fontSize={12}>
            {getUserOutOfOfficeNote(user.outOfOffice)}
          </Text>
        </HStack>
        <Text color={colors.gray50} fontWeight={400} fontSize={12}>
          <UserName userId={user.id} />
        </Text>
      </VStack>
    </HStack>
  );
};
